/**
 * SupplierFilterPills — Iter 157 (Phase 3)
 *
 * Used in Hub mode (no active supplier) at the top of /buyer/orders and
 * /buyer/shipping. Renders a horizontal scroll of pill chips, one per
 * connected supplier, with the count of in-scope items per supplier.
 *
 *   [All · 200]  [Acme · 47]  [BetaCo · 31]  [GammaCo · 14]
 *
 * Clicking a supplier pill calls `onPick(conn)` which the parent uses
 * to call `selectSupplier(conn)` from the SupplierContext — re-rendering
 * the list scoped to that supplier in one click. The All pill stays as
 * the default (no-op when nothing is scoped).
 *
 * Why: as buyers connect 10-20 suppliers, the cockpit list becomes a
 * scrolling wall. Pills give them a 1-click slicer without leaving the
 * page or opening a dropdown.
 */
import { useMemo } from "react";
import { Store } from "lucide-react";

export default function SupplierFilterPills({
  connections = [],
  orders = [],
  onPick,
  testidPrefix = "supplier-pill",
}) {
  // Group counts per supplier in one O(n) pass.
  const countsByConn = useMemo(() => {
    const m = new Map();
    for (const o of orders) {
      const sid = o.seller_id;
      if (!sid) continue;
      m.set(sid, (m.get(sid) || 0) + 1);
    }
    return m;
  }, [orders]);

  // Sort connections by count DESC (most active suppliers first) — gives
  // the buyer a visual signal of where their attention is needed.
  const sorted = useMemo(() => {
    return [...connections].sort((a, b) => {
      const ca = countsByConn.get(a.tenant_owner_user_id) || 0;
      const cb = countsByConn.get(b.tenant_owner_user_id) || 0;
      return cb - ca;
    });
  }, [connections, countsByConn]);

  if (!sorted.length) return null;

  return (
    <div
      className="flex items-center gap-2 flex-wrap py-2"
      data-testid={`${testidPrefix}-row`}
    >
      <div className="text-[10.5px] mono uppercase tracking-wider text-foreground/55 mr-1">
        Filter by supplier
      </div>
      <button
        type="button"
        disabled
        className="inline-flex items-center gap-1.5 rounded-full border-2 border-foreground bg-foreground text-background text-[12px] px-3 py-1"
        data-testid={`${testidPrefix}-all`}
      >
        <Store className="w-3 h-3"/>
        <span>All</span>
        <span className="mono opacity-80">{orders.length}</span>
      </button>
      {sorted.map((c) => {
        const n = countsByConn.get(c.tenant_owner_user_id) || 0;
        return (
          <button
            type="button"
            key={c.id || c.tenant_owner_user_id}
            onClick={() => onPick?.(c)}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white hover:bg-foreground hover:text-background text-[12px] px-3 py-1 transition-colors"
            data-testid={`${testidPrefix}-${c.tenant_owner_user_id}`}
            title={`Scope to ${c.tenant_name || "supplier"} (${n} order${n===1?"":"s"})`}
          >
            <span className="truncate max-w-[180px]">{c.tenant_name || "Supplier"}</span>
            <span className="mono text-foreground/55 group-hover:text-background/70">{n}</span>
          </button>
        );
      })}
    </div>
  );
}
